import * as THREE from 'three';
import { GLTFLoader } from 'three/addons/loaders/GLTFLoader.js';
import { MeshoptDecoder } from 'three/addons/libs/meshopt_decoder.module.js';
import { OrbitControls } from 'three/addons/controls/OrbitControls.js';
import { EffectComposer } from 'three/addons/postprocessing/EffectComposer.js';
import { RenderPass } from 'three/addons/postprocessing/RenderPass.js';
import { UnrealBloomPass } from 'three/addons/postprocessing/UnrealBloomPass.js';
import { OutputPass } from 'three/addons/postprocessing/OutputPass.js';
import { HOTSPOTS, type Hotspot } from './content';
import { Panels, panelWidth } from './panels';
import { GAME } from '../features';
import { SalmanOS } from './computer/os';
import type { AppId } from './computer/apps';
import { DEVICE } from '../device';
import { LivePrints, dhakaLabel } from './livePrints';
import { MODEL_BYTES } from './modelSizes';

// Phase E: Salman's Studio in the browser. The room comes from blender/scripts/studio/bake_export_studio.py: walls,
// furniture and props carry Cycles-baked lighting (rendered unlit), screens / neon / LEDs stay emissive and live.
// Hover outlines an object and names it, click flies the camera to it and opens its panel. Esc or Back returns.
// The computer (ultrawide, code monitor, PC) is different: the camera sits down in front of the ultrawide until it fills
// the view, and SalmanOS — a crisp HTML workspace — is pinned exactly onto the monitor's projected screen (or grows to
// full screen on small displays). Leaving it powers the screen down and stands back up.
// Axes (glTF, Y up): +X out of the left wall, +Z out of the back wall into the room.

// web/scripts/pack-studio.mjs makes two files of the same scene: phones get the atlases at the size they show them.
// index.html picks one (and preloads it) before the app runs; the device profile decides if it did not.
const PHONE = (document.documentElement.dataset.studioModel ?? (DEVICE.kind === 'phone' ? 'phone' : 'desktop')) === 'phone';
const URL = PHONE ? '/models/studio/salman_studio_phone.glb' : '/models/studio/salman_studio.glb';
const BACKGROUND = new THREE.Color('#0d1729');
// the hero camera of the Blender renders (CAM_Hero: 24 mm on a 36 mm sensor, 16:10)
const HERO_EYE = new THREE.Vector3(7.89, 4.17, 3.05);
const HERO_TARGET = new THREE.Vector3(2.6, 0.9, -2.95);
const HERO_FOV = 50;
// the two screens that run SalmanOS: the smallest projected size each is used at before going full screen, and the
// app a screen keeps pinned open (the vertical monitor is the GitHub browser)
type MonitorId = 'HS_Monitor_Ultrawide' | 'HS_Monitor_Code';
const MONITORS: Record<MonitorId, { min: [number, number]; pin?: AppId }> = {
  HS_Monitor_Ultrawide: { min: [860, 380] },
  HS_Monitor_Code: { min: [340, 560], pin: 'github' },
};
// the TV plays a muted loop of the game (web/scripts/record-teaser.mjs); clicking it, or the Drive button, dives into
// the screen and out into the city
const TEASER = '/studio/teaser.mp4';
// Nanoleaf-style hexagon lights: each panel drifts through the room's palette
const WALL_TOP = 2.8;   // the room's walls (build_studio.py H)
const HEX_PALETTE = ['#2fd3c4', '#ffb347', '#ff6b5a', '#8a7dff', '#3fa9ff'].map((c) => new THREE.Color(c));

type Group = { def: Hotspot; meshes: THREE.Mesh[]; box: THREE.Box3; mats: THREE.MeshBasicMaterial[]; dot?: HTMLButtonElement };
type Tween = { from: [THREE.Vector3, THREE.Vector3]; to: [THREE.Vector3, THREE.Vector3]; t: number; dur: number; done?: () => void };

function skyGradient(geometry: THREE.BufferGeometry) {
  const pos = geometry.getAttribute('position');
  geometry.computeBoundingBox();
  const { min, max } = geometry.boundingBox as THREE.Box3;
  const low = new THREE.Color('#4a2a16'), high = new THREE.Color('#0b1424'), c = new THREE.Color();
  const colors = new Float32Array(pos.count * 3);
  for (let i = 0; i < pos.count; i++) {
    const f = THREE.MathUtils.smoothstep((pos.getY(i) - min.y) / Math.max(max.y - min.y, 1e-3), 0.05, 0.85);
    c.copy(low).lerp(high, f).toArray(colors, i * 3);
  }
  geometry.setAttribute('color', new THREE.BufferAttribute(colors, 3));
}

const ease = (t: number) => (t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2);

/** Split a mesh into its connected pieces: the piece of every vertex and how many there are. Flat-shaded faces and UV
 *  seams split vertices along edges, so vertices sharing a position are welded first. */
function pieces(geo: THREE.BufferGeometry) {
  const pos = geo.getAttribute('position'), count = pos.count;
  const parent = Array.from({ length: count }, (_, i) => i);
  const find = (i: number): number => (parent[i] === i ? i : (parent[i] = find(parent[i])));
  const index = geo.getIndex();
  if (index) for (let i = 0; i < index.count; i += 3) { const a = find(index.getX(i)); parent[find(index.getX(i + 1))] = a; parent[find(index.getX(i + 2))] = a; }
  const key = new Map<string, number>();
  for (let i = 0; i < count; i++) {
    const k = `${pos.getX(i).toFixed(4)},${pos.getY(i).toFixed(4)},${pos.getZ(i).toFixed(4)}`;
    const j = key.get(k);
    if (j === undefined) key.set(k, i); else parent[find(i)] = find(j);
  }
  const roots = new Map<number, number>();
  const of = new Uint16Array(count);
  for (let i = 0; i < count; i++) { const r = find(i); if (!roots.has(r)) roots.set(r, roots.size); of[i] = roots.get(r) as number; }
  return { of, count: roots.size };
}

// the curtains (HS_Curtains): each linen panel is stretched from its outer edge to just past the middle of the window,
// its pleats flattening a little as the fabric spreads; the brass rings slide along the rod with it
type CurtainPanel = { mesh: THREE.Mesh; rest: Float32Array; side: Int8Array; ring: Float32Array };
const CURTAIN_OVERLAP = 0.03;   // m each panel reaches past the middle of the window
const CURTAIN_TIME = 1.6;       // s to draw them

export class Studio {
  readonly scene = new THREE.Scene();
  readonly camera = new THREE.PerspectiveCamera(HERO_FOV, innerWidth / innerHeight, 0.05, 80);
  readonly panels = new Panels();
  active = false;
  loaded = false;
  onExit: () => void = () => {};
  private renderer: THREE.WebGLRenderer;
  private controls: OrbitControls;
  private composer: EffectComposer;
  private bloom: UnrealBloomPass;
  private groups = new Map<string, Group>();
  private pickables: THREE.Mesh[] = [];
  private live: Array<{ mat: THREE.MeshBasicMaterial; base: THREE.Color; kind: string }> = [];
  private tween: Tween | null = null;
  private focused: Group | null = null;
  /** A panel with no object of its own (Skills, Work) is open: the whole room is framed beside it. */
  private panelOnly = false;
  private hovered: Group | null = null;
  private pointer = new THREE.Vector2();
  private pointerDirty = false;
  private down: { x: number; y: number } | null = null;
  private raycaster = new THREE.Raycaster();
  private time = 0;
  private enteredAt = 0;
  private savedToneMapping: THREE.ToneMapping = THREE.NoToneMapping;
  private loading: Promise<void> | null = null;
  private os: SalmanOS | null = null;
  private computerOn = false;
  private idleFrame = 0;
  private screenMat: { mat: THREE.MeshBasicMaterial; base: THREE.Color } | null = null;
  private monitor: MonitorId = 'HS_Monitor_Ultrawide';
  private hex = { plates: [] as THREE.Mesh[], on: false, level: 0, halo: null as THREE.Mesh | null };
  private teaser: HTMLVideoElement | null = null;
  private driving = false;
  private city = 0;   // how much of the city behind the studio has loaded (0..1)
  private lastMove = 0;   // when the view last changed (phones draw at half rate once it has been still for a while)
  private oddFrame = false;
  private livePrints: LivePrints;
  private curtains = { panels: [] as CurtainPanel[], closed: false, level: 0, sides: [] as Array<{ pivot: number; scale: number; depth: number }>, group: null as Group | null };
  private ui = {
    root: document.getElementById('studio-ui') as HTMLElement,
    label: document.getElementById('studio-label') as HTMLElement,
    dots: document.getElementById('studio-dots') as HTMLElement,
    hint: document.getElementById('studio-hint') as HTMLElement,
    drive: document.getElementById('studio-drive') as HTMLButtonElement,
    driveSub: document.getElementById('studio-drive-sub') as HTMLElement,
  };

  constructor(renderer: THREE.WebGLRenderer, private toast: (msg: string, ms?: number) => void) {
    this.renderer = renderer;
    this.scene.background = BACKGROUND;
    this.controls = new OrbitControls(this.camera, renderer.domElement);
    Object.assign(this.controls, {
      enableDamping: true, dampingFactor: 0.08, enablePan: false, rotateSpeed: 0.55, zoomSpeed: 0.7,
      minAzimuthAngle: THREE.MathUtils.degToRad(8), maxAzimuthAngle: THREE.MathUtils.degToRad(82),
      minPolarAngle: THREE.MathUtils.degToRad(42), maxPolarAngle: THREE.MathUtils.degToRad(80),
    });
    this.controls.enabled = false;
    if (DEVICE.touch) this.ui.hint.textContent = 'Drag to look around · pinch to zoom · tap anything that glows';
    this.composer = new EffectComposer(renderer);
    this.composer.addPass(new RenderPass(this.scene, this.camera));
    // only the live emissives (screens, neon, LEDs, RGB) go above 1.0, so only they bloom, as in the Eevee renders
    this.bloom = new UnrealBloomPass(new THREE.Vector2(innerWidth / 2, innerHeight / 2), 0.42, 0.4, 1.0);
    this.bloom.enabled = DEVICE.bloom;   // phones skip the full-screen bloom pass (fill rate, battery)
    this.composer.addPass(this.bloom);
    this.composer.addPass(new OutputPass());
    this.livePrints = new LivePrints(renderer);
    this.ui.drive.addEventListener('click', () => { this.ui.drive.blur(); void this.drive(); });
    this.setCityProgress(0);
    this.panels.onClose = () => { if (this.focused) this.back(); else if (this.panelOnly) this.framePanel(false); };
    // a panel's "open on the computer" sits you down at SalmanOS on that app
    this.panels.onAction = (a) => { if (a === 'exit') { if (GAME) this.exit(); else this.openPanel('drive'); } else if (a.startsWith('computer:')) { this.panels.close(); void this.openComputer(a.slice(9) as AppId); } };

    const el = renderer.domElement;
    el.addEventListener('pointermove', (e) => { if (!this.active) return; this.lastMove = this.time; this.setPointer(e); this.pointerDirty = true; });
    el.addEventListener('wheel', () => { this.lastMove = this.time; }, { passive: true });
    el.addEventListener('pointerdown', (e) => { if (this.active) this.down = { x: e.clientX, y: e.clientY }; });
    el.addEventListener('pointerup', (e) => {
      if (!this.active || !this.down) return;
      const moved = Math.hypot(e.clientX - this.down.x, e.clientY - this.down.y);
      this.down = null;
      if (moved > 6 || this.computerOn) return;   // an orbit drag, or the computer has the pointer
      this.setPointer(e);
      const g = this.pick();
      if (g) this.activate(g);
      else if (this.focused) this.panels.close();
    });
    el.addEventListener('pointerleave', () => { if (this.active) this.setHover(null); });
    addEventListener('keydown', (e) => {
      if (!this.active || e.key !== 'Escape' || this.computerOn) return;
      if (this.focused) this.panels.close(); else if (GAME) this.exit();
    });
    addEventListener('resize', () => this.resize());
  }

  /** Fetch and prepare the room (idempotent); resolves when it can be shown. */
  load(progress?: (f: number) => void) {
    // a compressed (brotli) response has no length, so the known size of the file stands in
    const expected = PHONE ? MODEL_BYTES.phone : MODEL_BYTES.desktop;
    const report = (f: number) => {
      progress?.(f);
      document.documentElement.style.setProperty('--studio-load', f.toFixed(3));   // the landing's gold line and the still behind it
      const text = document.querySelector('.studio-first #loading > span');
      if (text) text.textContent = `Opening the studio ${Math.round(f * 100)}%`;
    };
    this.loading ??= new GLTFLoader().setMeshoptDecoder(MeshoptDecoder).loadAsync(URL, (e) => report(Math.min(0.99, e.loaded / (e.total || expected)))).then(async (gltf) => {
      report(1);
      const root = gltf.scene;
      root.traverse((o) => {
        const mesh = o as THREE.Mesh;
        if (!mesh.isMesh) return;
        // packed meshes sit on an unnamed child node that carries their quantization grid (GLTFLoader calls them
        // mesh_<n>): they answer to their named parent's name
        if (/^mesh_\d+(_\d+)?$/.test(mesh.name) && mesh.parent?.name) mesh.name = mesh.parent.name;
        mesh.material = this.convert(mesh.material as THREE.MeshStandardMaterial);
        if ((mesh.material as THREE.Material).name === 'STU_NightSky') skyGradient(mesh.geometry);
        mesh.matrixAutoUpdate = false;
        mesh.updateMatrix();
      });
      this.scene.add(root);
      root.updateMatrixWorld(true);
      await this.fitTextures(root);
      for (const def of HOTSPOTS) {
        const meshes: THREE.Mesh[] = [];
        root.traverse((o) => { if ((o as THREE.Mesh).isMesh && (o.name === def.id || o.name.startsWith(`${def.id}__`))) meshes.push(o as THREE.Mesh); });
        if (!meshes.length) continue;
        // own material copies, so hovering one object does not light up everything sharing its atlas
        const mats: THREE.MeshBasicMaterial[] = [];
        for (const m of meshes) {
          if ((m.material as THREE.Material).name === 'STU_HexLight') { this.prepareHex(m); this.pickables.push(m); continue; }
          const own = (m.material as THREE.MeshBasicMaterial).clone();
          own.userData.base = own.color.clone();
          m.material = own; mats.push(own);
          this.pickables.push(m);
        }
        const box = new THREE.Box3();
        meshes.forEach((m) => box.expandByObject(m));
        const g: Group = { def, meshes, box, mats };
        meshes.forEach((m) => { m.userData.hotspot = def.id; });
        this.groups.set(def.id, g);
        if (def.toggle === 'curtains') this.prepareCurtains(g);
        if (def.dot) {
          const dot = document.createElement('button');
          dot.className = 'studio-dot';
          dot.setAttribute('aria-label', def.label);
          const label = document.createElement('span');
          label.textContent = def.label;
          dot.append(document.createElement('i'), label);
          dot.addEventListener('click', () => this.activate(g));
          this.ui.dots.appendChild(dot);
          g.dot = dot;
        }
      }
      // picture glass is anti-reflective museum glass: barely there, so the portrait and prints keep their contrast
      // (the PC's and the fridge's glass keep the default tint)
      for (const id of ['HS_Portrait', 'HS_HeroPoster', 'HS_Jersey']) {
        for (const m of this.groups.get(id)?.meshes ?? []) if ((m.material as THREE.Material).name === 'STU_Glass') (m.material as THREE.MeshBasicMaterial).opacity = 0.035;
      }
      this.setupTeaser();
      const find = (id: string, mat: string) => this.groups.get(id)?.meshes.find((m) => (m.material as THREE.Material).name === mat || m.name.endsWith(`__${mat}`));
      this.livePrints.attach(find('HS_Calendar', 'STU_CalendarPrint'), find('HS_Clock', 'STU_ClockFace'), find('HS_Monitor_Ultrawide', 'STU_ScreenCatalogue'));
      this.loaded = true;
    });
    return this.loading;
  }

  /** Phones (and low-memory tablets) keep the 4K baked atlases at 2K on the GPU: a 4K RGBA atlas with mipmaps is
   *  ~85 MB of video memory, three of them can take a mobile browser down. Downloads stay the same file. */
  private async fitTextures(root: THREE.Object3D) {
    const max = DEVICE.maxTexture;
    const seen = new Set<THREE.Texture>();
    root.traverse((o) => {
      const map = ((o as THREE.Mesh).material as THREE.MeshBasicMaterial | undefined)?.map;
      if (map) seen.add(map);
    });
    await Promise.all([...seen].map(async (tex) => {
      const img = tex.image as { width: number; height: number } | undefined;
      if (!img || Math.max(img.width, img.height) <= max || typeof createImageBitmap === 'undefined') return;
      const s = max / Math.max(img.width, img.height);
      try {
        tex.image = await createImageBitmap(tex.image as ImageBitmapSource, { resizeWidth: Math.round(img.width * s), resizeHeight: Math.round(img.height * s), resizeQuality: 'high' });
        tex.needsUpdate = true;
      } catch { /* keep the full-size texture */ }
    }));
  }

  private convert(src: THREE.MeshStandardMaterial): THREE.MeshBasicMaterial {
    const name = src.name ?? '';
    const side = THREE.DoubleSide;
    if (name.startsWith('BAKED_')) {
      const map = src.map;
      if (map) { map.colorSpace = THREE.SRGBColorSpace; map.anisotropy = this.renderer.capabilities.getMaxAnisotropy(); }   // sharp at grazing angles
      return Object.assign(new THREE.MeshBasicMaterial({ map, side, toneMapped: false }), { name });
    }
    if (name === 'STU_Glass' || name === 'STU_WindowGlass') {
      return Object.assign(new THREE.MeshBasicMaterial({ color: name === 'STU_Glass' ? '#9fc2cc' : '#6f8ea6', transparent: true, opacity: name === 'STU_Glass' ? 0.16 : 0.07, depthWrite: false, side, toneMapped: false }), { name });
    }
    if (name === 'STU_BoxDark') return Object.assign(new THREE.MeshBasicMaterial({ color: BACKGROUND, toneMapped: false }), { name });
    if (name === 'STU_NightSky') {
      // vertex colours set per mesh in load(): sodium haze over the city fading up into navy
      return Object.assign(new THREE.MeshBasicMaterial({ vertexColors: true, toneMapped: false }), { name });
    }
    // live emissive: the texture (screens, neon, skyline cards) or the colour, pushed above 1.0 so it blooms
    const map = src.emissiveMap ?? src.map;
    if (map) map.colorSpace = THREE.SRGBColorSpace;
    const strength = src.emissiveIntensity ?? 1;
    const gain: Record<string, number> = {
      STU_ScreenCatalogue: 1.08, STU_ScreenCode: 1.1, STU_ScreenTV: 1.05, STU_PhoneScreen: 1.1, STU_NeonBangla: 1.35,
      STU_Skyline0: 1.0, STU_Skyline1: 1.0, STU_Skyline2: 1.0, STU_Moon: 2.0, STU_LampShade: 1.05,
      // the live prints: the calendar is paper under the slat wall's warm wash, the clock's digits glow (and bloom)
      STU_CalendarPrint: 0.88, STU_ClockFace: 1.3,
    };
    const k = gain[name] ?? Math.min(strength, 3) * 0.6;
    const color = map ? new THREE.Color(k, k, k) : src.emissive.clone().multiplyScalar(k);
    const transparent = src.transparent || /Neon|Skyline/.test(name);
    const mat = new THREE.MeshBasicMaterial({ map, color, transparent, alphaTest: transparent ? 0.02 : 0, depthWrite: !transparent, side, toneMapped: false });
    mat.name = name;
    this.live.push({ mat, base: color.clone(), kind: name });
    return mat;
  }

  // ------------------------------------------------------------------ camera

  private heroPose(): [THREE.Vector3, THREE.Vector3] {
    // keep the whole room in frame on narrow screens: back the camera off along the hero sight line
    const aspect = this.camera.aspect;
    const off = HERO_EYE.clone().sub(HERO_TARGET);
    if (aspect < 0.8) {
      // portrait phones and tablets: look down more steeply, so the room's floor plan stands taller in the frame, and
      // let the plinth's far corners crop a little rather than shrink the room to a strip
      const sph = new THREE.Spherical().setFromVector3(off);
      sph.phi = THREE.MathUtils.degToRad(52);
      off.setFromSpherical(sph);
      return [HERO_TARGET.clone().add(off.multiplyScalar(Math.max(1, 1.06 / aspect))), HERO_TARGET.clone()];
    }
    return [HERO_TARGET.clone().add(off.multiplyScalar(Math.max(1, 1.165 / aspect))), HERO_TARGET.clone()];
  }

  private focusPose(g: Group): [THREE.Vector3, THREE.Vector3] {
    const c = g.box.getCenter(new THREE.Vector3());
    const size = g.box.getSize(new THREE.Vector3());
    const dir = new THREE.Vector3(...g.def.view).normalize();
    const vfov = THREE.MathUtils.degToRad(this.camera.fov);
    const visibleW = this.camera.aspect - (this.panelOffset() * 2) / innerHeight;
    const visibleH = 1 - (this.sheetOffset() * 2) / innerHeight;   // phones: only the strip above the bottom sheet
    const extent = Math.max(size.y, Math.hypot(size.x, size.z) / Math.max(visibleW, 0.6));
    let dist = Math.max(0.35, extent / ((g.def.fill ?? 0.5) * visibleH) / (2 * Math.tan(vfov / 2)));
    // never park the camera behind something solid (the chair in front of the desk): stop short of it
    this.raycaster.set(c, dir);
    this.raycaster.far = dist;
    for (const h of this.raycaster.intersectObjects(this.scene.children, true)) {
      const mesh = h.object as THREE.Mesh;
      if (!mesh.isMesh || mesh.userData.hotspot === g.def.id || (mesh.material as THREE.Material).transparent) continue;
      if (h.distance > extent * 0.5) { dist = Math.max(0.3, h.distance - 0.12); break; }
    }
    this.raycaster.far = Infinity;
    return [c.clone().addScaledVector(dir, dist), c];
  }

  private panelOffset() { return panelWidth() / 2; }   // panels.ts: the panel's width on this screen (0 = bottom sheet)
  /** Narrow screens open panels as a bottom sheet (62vh, studio.css): lift the focused object into the space above. */
  private sheetOffset() { return innerWidth <= 700 ? innerHeight * 0.31 : 0; }

  private fly(to: [THREE.Vector3, THREE.Vector3], dur: number, done?: () => void) {
    this.tween = { from: [this.camera.position.clone(), this.controls.target.clone()], to, t: 0, dur, done };
  }

  private applyViewOffset(on: boolean) {
    // when a panel is open on a wide screen, shift the projection so the focused object sits in the free left part
    const off = on ? this.panelOffset() : 0;
    const lift = on ? this.sheetOffset() : 0;
    if (off || lift) this.camera.setViewOffset(innerWidth, innerHeight, off, lift, innerWidth, innerHeight);
    else this.camera.clearViewOffset();
  }

  // ------------------------------------------------------------------ interaction

  private setPointer(e: PointerEvent) {
    const r = this.renderer.domElement.getBoundingClientRect();
    this.pointer.set(((e.clientX - r.left) / r.width) * 2 - 1, -((e.clientY - r.top) / r.height) * 2 + 1);
    this.ui.label.style.transform = `translate(${e.clientX + 14}px, ${e.clientY + 14}px)`;
  }

  private pick(): Group | null {
    this.raycaster.setFromCamera(this.pointer, this.camera);
    for (const h of this.raycaster.intersectObjects(this.scene.children, true)) {
      const mesh = h.object as THREE.Mesh;
      if (!mesh.isMesh) continue;
      const id = mesh.userData.hotspot as string | undefined;
      if (id) return this.groups.get(id) ?? null;
      if ((mesh.material as THREE.Material).transparent) continue;   // look through glass and the window
      return null;   // something solid is in front
    }
    return null;
  }

  private setHover(g: Group | null) {
    if (g === this.hovered) return;
    if (this.hovered) this.hovered.mats.forEach((m) => m.color.copy(m.userData.base as THREE.Color));
    this.hovered = g;
    if (g) g.mats.forEach((m) => m.color.copy(m.userData.base as THREE.Color).multiplyScalar(1.35));
    this.ui.label.hidden = !g;
    if (g) this.ui.label.textContent = g.def.clock ? `Dhaka · ${dhakaLabel().split(' · ')[0]}` : g.def.label;
    this.renderer.domElement.style.cursor = g ? 'pointer' : '';
  }

  private activate(g: Group) {
    if (g.def.drive && GAME) { void this.drive(); return; }   // not live yet: the TV opens its panel (Coming soon)
    if (g.def.clock) { this.toast(`Dhaka · ${dhakaLabel()} (GMT+6)`, 3600); return; }
    if (g.def.toggle === 'hexLights') { this.toggleHex(); return; }
    if (g.def.toggle === 'curtains') { this.toggleCurtains(); return; }
    if (g.def.toast) { this.toast(g.def.toast, 4200); return; }
    if (g.def.computer) { void this.openComputer(g.def.computer, g.def.id === 'HS_Monitor_Code' ? 'HS_Monitor_Code' : 'HS_Monitor_Ultrawide'); return; }
    if (!g.def.panel) return;
    this.focused = g;
    this.setHover(null);
    this.controls.enabled = false;
    this.ui.root.classList.add('focused');
    this.applyViewOffset(true);
    this.fly(this.focusPose(g), 1.1);
    this.panels.open(g.def.panel);
  }

  /** Close-up → overview. */
  back() {
    this.focused = null;
    this.ui.root.classList.remove('focused');
    this.applyViewOffset(false);
    this.fly(this.heroPose(), 1.0, () => { this.controls.enabled = true; });
  }

  /** Open a panel straight away (menu / Contact button), focusing its object when there is one. */
  openPanel(id: Hotspot['panel']) {
    const g = [...this.groups.values()].find((x) => x.def.panel === id);
    if (g && this.active) this.activate(g);
    else if (id) { this.panels.open(id); if (this.active && !this.focused && !this.computerOn) this.framePanel(true); }
  }

  /** Frame the whole room in the space a panel leaves free (left of the side panel, above the phone sheet), pulling the
   *  camera back by how much of the screen is covered; the studio's own buttons step aside, as for a focused object. */
  private framePanel(on: boolean) {
    this.panelOnly = on;
    this.ui.root.classList.toggle('focused', on);
    this.applyViewOffset(on);
    const [eye, target] = this.heroPose();
    if (on) {
      const free = Math.min(1 - (2 * this.panelOffset()) / innerWidth, 1 - (2 * this.sheetOffset()) / innerHeight);
      eye.sub(target).multiplyScalar(THREE.MathUtils.clamp(1 / Math.max(free, 0.1), 1, 2.4)).add(target);
      this.controls.enabled = false;
    }
    this.fly([eye, target], on ? 0.9 : 1.0, () => { if (!on) this.controls.enabled = true; });
  }

  /** Test hook: focus a hotspot by id, and the state the browser checks read. */
  debugFocus(id: string) { const g = this.groups.get(id); if (g) this.activate(g); return Boolean(g); }
  debugComputer() { return { on: this.computerOn, booting: Boolean(this.os?.active), rect: this.os ? this.os.root.getBoundingClientRect().toJSON() : null }; }
  debugHex() { return { on: this.hex.on, level: this.hex.level, plates: this.hex.plates.length, panels: this.hex.plates.map((m) => m.userData.panels as number) }; }
  debugCurtains() { return { closed: this.curtains.closed, level: this.curtains.level, meshes: this.curtains.panels.length, sides: this.curtains.sides }; }
  debugInfo() {
    const r = this.renderer.info.render;
    return { teaser: this.teaser ? { playing: !this.teaser.paused, time: +this.teaser.currentTime.toFixed(2), ready: this.teaser.readyState } : null, city: this.city, hex: this.debugHex(), curtains: this.debugCurtains(), computer: this.computerOn ? this.monitor : null, active: this.active, loaded: this.loaded, focused: this.focused?.def.id ?? null, tweening: Boolean(this.tween), panel: this.panels.current, hotspots: [...this.groups.keys()], calls: r.calls, triangles: r.triangles };
  }

  // ------------------------------------------------------------------ the computer

  /** Camera square-on in front of a monitor, close enough that its screen fills ~90 % of the view. The screen's corners
   *  come from its UVs (u right, v down in glTF), so this works for the curved ultrawide (corners at its edges' depth)
   *  and for the vertical monitor turned towards the chair alike. */
  private screenFrame(id: string = this.monitor) {
    const screen = this.groups.get(id)?.meshes.find((m) => /__STU_Screen/.test(m.name));
    if (!screen) return null;
    const geo = screen.geometry, pos = geo.getAttribute('position'), uv = geo.getAttribute('uv');
    const corner = (u: number, v: number) => {
      let best = 0, bd = Infinity;
      for (let i = 0; i < uv.count; i++) { const d = (uv.getX(i) - u) ** 2 + (uv.getY(i) - v) ** 2; if (d < bd) { bd = d; best = i; } }
      return new THREE.Vector3().fromBufferAttribute(pos, best).applyMatrix4(screen.matrixWorld);
    };
    const tl = corner(0, 0), tr = corner(1, 0), bl = corner(0, 1), br = corner(1, 1);
    const right = tr.clone().sub(tl), up = tl.clone().sub(bl);
    const w = right.length(), h = up.length();
    const n = right.clone().cross(up).normalize();   // out of the screen, towards the viewer
    const c = tl.clone().add(br).multiplyScalar(0.5);
    const t = 2 * Math.tan(THREE.MathUtils.degToRad(this.camera.fov) / 2);
    const d = Math.max(w / (0.92 * t * this.camera.aspect), h / (0.86 * t));
    return { eye: c.clone().addScaledVector(n, d), target: c, corners: [tl, br], screen, widthM: w };
  }

  private isFull(rect: { width: number; height: number }) {
    const [mw, mh] = MONITORS[this.monitor].min;
    return rect.width < mw || rect.height < mh;
  }

  /** The monitor's screen in CSS pixels from the current camera. */
  private screenRect(corners: THREE.Vector3[]) {
    const [a, b] = corners.map((v) => v.clone().project(this.camera));
    const x0 = ((a.x + 1) / 2) * innerWidth, y0 = ((1 - a.y) / 2) * innerHeight, x1 = ((b.x + 1) / 2) * innerWidth, y1 = ((1 - b.y) / 2) * innerHeight;
    return { left: Math.round(x0), top: Math.round(y0), width: Math.round(x1 - x0), height: Math.round(y1 - y0) };
  }

  async openComputer(app: AppId | 'home' = 'home', monitor: MonitorId = 'HS_Monitor_Ultrawide') {
    if (this.computerOn) return;
    await this.load();
    this.monitor = monitor;
    const f = this.screenFrame();
    if (!f) return;
    this.computerOn = true;
    this.os ??= new SalmanOS();
    this.os.onExit = () => { void this.closeComputer(); };
    if (this.panels.isOpen) { this.focused = null; this.panels.close(); }
    this.setHover(null);
    this.controls.enabled = false;
    this.applyViewOffset(false);
    this.ui.root.classList.add('computer');
    this.returnPose = [this.camera.position.clone(), this.controls.target.clone()];
    this.fly([f.eye, f.target], 1.25, () => {
      this.camera.lookAt(f.target);
      const rect = this.screenRect(f.corners);
      const full = this.isFull(rect);
      const os = this.os as SalmanOS;
      const bezel = (rect.width * 0.008) / f.widthM;   // 8 mm of matte bezel each side
      os.setRect(rect, false, bezel);
      // the 3D screen behind it dims, so no glowing sliver shows at the curved panel's edges
      const mat = f.screen.material as THREE.MeshBasicMaterial;
      mat.userData.screenBase ??= mat.color.clone();
      this.screenMat = { mat, base: mat.userData.screenBase as THREE.Color };
      mat.color.copy(this.screenMat.base).multiplyScalar(0.05);
      if (full) requestAnimationFrame(() => requestAnimationFrame(() => os.setRect({ left: 0, top: 0, width: innerWidth, height: innerHeight }, true)));
      const pin = MONITORS[monitor].pin;
      void os.start(app === pin ? 'home' : app, pin);
    });
  }

  private returnPose: [THREE.Vector3, THREE.Vector3] | null = null;
  private async closeComputer() {
    if (!this.computerOn || !this.os) return;
    await this.os.shutdown();
    if (this.screenMat) this.screenMat.mat.color.copy(this.screenMat.base);
    this.ui.root.classList.remove('computer');
    this.fly(this.returnPose ?? this.heroPose(), 1.1, () => { this.computerOn = false; this.controls.enabled = true; });
  }

  // ------------------------------------------------------------------ the game

  /** The TV's screen plays the teaser (muted, looping, inline, so every browser lets it autoplay); the painted still
   *  stays on until the first frame has arrived. */
  private setupTeaser() {
    const screen = this.groups.get('HS_TV')?.meshes.find((m) => /__STU_ScreenTV/.test(m.name));
    if (!screen) return;
    const video = Object.assign(document.createElement('video'), { src: TEASER, muted: true, loop: true, playsInline: true, preload: 'auto' });
    video.setAttribute('playsinline', '');
    video.addEventListener('loadeddata', () => {
      const tex = GAME ? new THREE.VideoTexture(video) : this.comingSoonTexture(video);
      tex.colorSpace = THREE.SRGBColorSpace;
      tex.flipY = false;   // glTF UVs
      const mat = screen.material as THREE.MeshBasicMaterial;
      mat.map = tex;
      mat.needsUpdate = true;
    }, { once: true });
    this.teaser = video;
    if (this.active) void video.play().catch(() => {});
  }

  /** While the game is not live the teaser's own caption ("Drive Dhaka · click the TV to play", recorded into the video)
   *  is covered by "Coming soon": each new video frame is drawn to a canvas with the new caption over the old one. */
  private comingSoonTexture(video: HTMLVideoElement) {
    const W = 960, H = 540;
    const c = Object.assign(document.createElement('canvas'), { width: W, height: H });
    const g = c.getContext('2d') as CanvasRenderingContext2D;
    const tex = new THREE.CanvasTexture(c);
    const draw = () => {
      g.drawImage(video, 0, 0, W, H);
      const shade = g.createLinearGradient(0, 330, 0, 430);
      shade.addColorStop(0, 'rgba(8,12,12,0)'); shade.addColorStop(1, 'rgba(8,12,12,.96)');
      g.fillStyle = shade; g.fillRect(0, 330, W, 100);
      g.fillStyle = 'rgba(8,12,12,.96)'; g.fillRect(0, 430, W, H - 430);
      const label = 'THE GAME · IN DEVELOPMENT';
      g.font = '600 17px "Panel Mono", Consolas, monospace';
      g.fillStyle = '#e9b45e'; g.fillRect(48, 446, g.measureText(label).width + 20, 30);
      g.fillStyle = '#0c0f0e'; g.fillText(label, 58, 467);
      g.fillStyle = '#ece6d8'; g.font = '760 56px "Panel Grotesk", "Segoe UI", sans-serif'; g.fillText('COMING SOON', 46, 528);
      tex.needsUpdate = true;
    };
    const rvfc = (video as HTMLVideoElement & { requestVideoFrameCallback?: (cb: () => void) => number }).requestVideoFrameCallback?.bind(video);
    if (rvfc) { const loop = () => { draw(); rvfc(loop); }; rvfc(loop); }
    else setInterval(() => { if (!video.paused) draw(); }, 40);
    draw();
    return tex;
  }

  /** The Drive button: the ring fills as the city loads behind the studio. */
  setCityProgress(f: number) {
    if (!GAME) { this.ui.drive.classList.add('soon'); this.ui.driveSub.textContent = 'Coming soon · in development'; return; }
    this.city = Math.max(this.city, Math.min(1, f));
    const ready = this.city >= 1;
    (this.ui.drive.querySelector('.ring') as HTMLElement).style.setProperty('--p', this.city.toFixed(3));
    this.ui.drive.classList.toggle('ready', ready);
    this.ui.driveSub.textContent = !ready ? `Loading the city… ${Math.round(this.city * 100)}%`
      : DEVICE.kind === 'phone' || DEVICE.kind === 'tablet' ? 'Touch controls · best on desktop' : 'Play the game ▸';
  }

  /** Into the game: the camera turns square on to the TV, dives through its screen, and the city fades in. */
  async drive() {
    if (!GAME) { this.openPanel('drive'); return; }   // the game is not live yet: Coming soon
    if (!this.active || this.computerOn || this.driving) return;
    this.driving = true;
    if (this.panels.isOpen) { this.focused = null; this.panels.close(); }
    this.setHover(null);
    this.controls.enabled = false;
    this.applyViewOffset(false);
    this.ui.root.classList.add('driving');
    const out = () => { this.driving = false; this.ui.root.classList.remove('driving'); this.exit(); };
    const f = this.screenFrame('HS_TV');
    if (!f) { out(); return; }
    const through = f.target.clone().lerp(f.eye, 0.3);
    this.fly([f.eye, f.target], 1.0, () => this.fly([through, f.target], 0.5, out));
  }

  // ------------------------------------------------------------------ enter / exit

  async enter() {
    await this.load();
    if (this.active) return;
    this.active = true;
    this.enteredAt = this.time;
    this.savedToneMapping = this.renderer.toneMapping;
    this.renderer.toneMapping = THREE.NoToneMapping;   // the bakes already went through AgX in Blender
    // the room is static and close to the camera: draw it at the screen's full pixel density (the city caps it lower)
    this.renderer.setPixelRatio(DEVICE.studioPixelRatio);
    this.resize();
    this.ui.root.hidden = false;
    void this.teaser?.play().catch(() => {});
    this.ui.hint.classList.remove('gone');
    window.setTimeout(() => this.ui.hint.classList.add('gone'), 6000);
    // glide in from the doorway: start further out and higher, settle on the hero view
    const [eye, target] = this.heroPose();
    const start = target.clone().add(eye.clone().sub(target).multiplyScalar(1.9)).add(new THREE.Vector3(0, 2.5, 0));
    this.camera.position.copy(start);
    this.controls.target.copy(target);
    this.camera.lookAt(target);
    this.controls.enabled = false;
    this.fly([eye, target], 1.6, () => { this.controls.enabled = true; });
  }

  exit() {
    if (!this.active) return;
    if (this.computerOn) { void this.os?.shutdown(); this.computerOn = false; this.ui.root.classList.remove('computer'); if (this.screenMat) this.screenMat.mat.color.copy(this.screenMat.base); }
    if (this.panels.isOpen) { this.focused = null; this.panels.close(); }
    this.applyViewOffset(false);
    this.setHover(null);
    this.active = false;
    this.controls.enabled = false;
    this.renderer.toneMapping = this.savedToneMapping;
    this.renderer.setPixelRatio(DEVICE.cityPixelRatio);   // the city's own cap (the resolution governor scales it)
    this.teaser?.pause();
    this.ui.root.hidden = true;
    this.ui.root.classList.remove('focused');
    this.onExit();
  }

  private resize() {
    this.camera.aspect = innerWidth / innerHeight;
    this.camera.updateProjectionMatrix();
    this.composer.setSize(innerWidth, innerHeight);
    this.composer.setPixelRatio(this.renderer.getPixelRatio());
    this.bloom.resolution.set(innerWidth / 2, innerHeight / 2);
    if (this.active && this.focused) this.applyViewOffset(true);
    // the computer stays pinned to the monitor: re-seat the camera and the screen for the new window size
    if (this.computerOn && this.os && !this.tween) {
      const f = this.screenFrame();
      if (f) {
        this.camera.position.copy(f.eye); this.controls.target.copy(f.target); this.camera.lookAt(f.target);
        const rect = this.screenRect(f.corners);
        const full = this.isFull(rect);
        this.os.setRect(full ? { left: 0, top: 0, width: innerWidth, height: innerHeight } : rect, full, (rect.width * 0.008) / f.widthM);
      }
    }
    const [heroEye, heroTarget] = this.heroPose();
    this.controls.minDistance = 2.2;
    this.controls.maxDistance = heroEye.distanceTo(heroTarget) * 1.12;
  }

  // ------------------------------------------------------------------ frame

  update(dt: number) {
    this.time += dt;
    if (!this.active) return;
    if (this.tween) {
      const tw = this.tween;
      tw.t = Math.min(1, tw.t + dt / tw.dur);
      const e = ease(tw.t);
      this.camera.position.lerpVectors(tw.from[0], tw.to[0], e);
      this.controls.target.lerpVectors(tw.from[1], tw.to[1], e);
      this.camera.lookAt(this.controls.target);
      if (tw.t >= 1) { this.tween = null; tw.done?.(); }
    } else if (this.controls.enabled) {
      if (this.controls.update()) this.lastMove = this.time;
    }
    if (this.tween || this.down || this.curtains.level % 1 !== 0 || (this.hex.level > 0 && this.hex.level < 1)) this.lastMove = this.time;
    if (this.pointerDirty && !this.focused && !this.tween && !this.computerOn) { this.pointerDirty = false; this.setHover(this.pick()); }
    this.animate(dt);
    this.placeDots();
    // phones: a still view is drawn at half rate (30 fps on a 60 Hz screen) to save battery and heat; any movement
    // brings full rate straight back
    this.oddFrame = !this.oddFrame;
    if (DEVICE.kind === 'phone' && this.time - this.lastMove > 1.2 && this.oddFrame) return;
    // sitting at the computer the camera is still and the interface covers the monitor (or the whole screen): the room is
    // drawn at ~10 fps, not at all under a full-screen computer, so the GPU and the main thread belong to the interface
    if (this.computerOn && !this.tween && (this.os?.root.classList.contains('full') || ++this.idleFrame % 6)) return;
    this.composer.render(dt);
  }

  private animate(dt: number) {
    const t = this.time, since = t - this.enteredAt;
    this.animateHex(t);
    this.animateCurtains(dt);
    this.livePrints.update();
    for (const l of this.live) {
      if (l.kind === 'STU_RGB') {
        // slow breathing on the PC fans and strips
        l.mat.color.copy(l.base).multiplyScalar(0.75 + 0.25 * Math.sin(t * 1.6));
      } else if (l.kind === 'STU_NeonBangla') {
        // the neon stutters on as you walk in, then holds
        const on = since > 1.4 || (Math.sin(since * 47) > 0.2 && Math.sin(since * 13) > -0.4);
        l.mat.color.copy(l.base).multiplyScalar(on ? 1 : 0.12);
      }
    }
  }

  // ------------------------------------------------------------------ hexagon lights

  /** The diffusers come in as one mesh: split it into its panels (connected pieces) and give each its own colour. */
  private prepareHex(mesh: THREE.Mesh) {
    const geo = mesh.geometry, count = geo.getAttribute('position').count;
    const { of: panelOf, count: panels } = pieces(geo);
    geo.setAttribute('color', new THREE.BufferAttribute(new Float32Array(count * 3), 3));
    mesh.userData.panelOf = panelOf;
    mesh.userData.panels = panels;
    mesh.material = Object.assign(new THREE.MeshBasicMaterial({ vertexColors: true, toneMapped: false, transparent: true, opacity: 0, side: THREE.DoubleSide }), { name: 'STU_HexLight' });
    mesh.visible = false;
    this.hex.plates.push(mesh);
    // the glow they throw on the wall: a soft additive disc just in front of the plaster, behind the panels
    if (!this.hex.halo) {
      const box = new THREE.Box3().setFromObject(mesh), c = box.getCenter(new THREE.Vector3()), size = box.getSize(new THREE.Vector3());
      const cv = document.createElement('canvas'); cv.width = cv.height = 256;
      const g = cv.getContext('2d') as CanvasRenderingContext2D, grd = g.createRadialGradient(128, 128, 0, 128, 128, 128);
      grd.addColorStop(0, 'rgba(255,255,255,.85)'); grd.addColorStop(0.45, 'rgba(255,255,255,.32)'); grd.addColorStop(1, 'rgba(255,255,255,0)');
      g.fillStyle = grd; g.fillRect(0, 0, 256, 256);
      // a square glow, cut off at the top of the wall (2.8 m) so it never floats above the cut-away room
      const r = Math.max(size.z, size.y) * 2.1, top = Math.min(c.y + r / 2, WALL_TOP - 0.01), h = top - (c.y - r / 2);
      const map = new THREE.CanvasTexture(cv);
      map.repeat.set(1, h / r);
      const halo = new THREE.Mesh(new THREE.PlaneGeometry(r, h), new THREE.MeshBasicMaterial({ map, transparent: true, opacity: 0, depthWrite: false, blending: THREE.AdditiveBlending, toneMapped: false }));
      halo.rotation.y = Math.PI / 2;   // facing +X, out of the left wall
      halo.position.set(0.004, top - h / 2, c.z);
      halo.visible = false;
      halo.renderOrder = 2;
      this.scene.add(halo);
      this.hex.halo = halo;
    }
  }

  private toggleHex() {
    this.hex.on = !this.hex.on;
    this.toast(this.hex.on ? 'Hex lights on' : 'Hex lights off', 1400);
  }

  private animateHex(t: number) {
    const hx = this.hex;
    if (!hx.plates.length || (!hx.on && hx.level === 0)) return;
    const target = hx.on ? 1 : 0;
    hx.level += Math.sign(target - hx.level) * Math.min(Math.abs(target - hx.level), 1 / 20);   // ~0.35 s fade at 60 fps
    const visible = hx.level > 0.001;
    const avg = new THREE.Color();
    for (const m of hx.plates) {
      m.visible = visible;
      (m.material as THREE.MeshBasicMaterial).opacity = Math.min(1, hx.level * 1.2);
      const col = m.geometry.getAttribute('color') as THREE.BufferAttribute, panelOf = m.userData.panelOf as Uint16Array, n = m.userData.panels as number;
      const colours = Array.from({ length: n }, (_, p) => {
        const f = ((t * 0.12 + p * 0.37) % 1) * HEX_PALETTE.length, i = Math.floor(f);
        return HEX_PALETTE[i].clone().lerp(HEX_PALETTE[(i + 1) % HEX_PALETTE.length], f - i).multiplyScalar(1.5 * hx.level);
      });
      for (let i = 0; i < panelOf.length; i++) { const c = colours[panelOf[i]]; col.setXYZ(i, c.r, c.g, c.b); }
      col.needsUpdate = true;
      colours.forEach((c) => avg.add(c));
      avg.multiplyScalar(1 / Math.max(1, n));
    }
    if (hx.halo) {
      hx.halo.visible = visible;
      const mat = hx.halo.material as THREE.MeshBasicMaterial;
      mat.color.copy(avg).multiplyScalar(0.55);
      mat.opacity = hx.level;
    }
  }

  // ------------------------------------------------------------------ curtains

  /** Sort the curtain mesh's pieces into the two linen panels (the tall ones) and their rings, left or right of the
   *  window's middle, and work out how far each panel has to stretch to meet the other. */
  private prepareCurtains(g: Group) {
    type Piece = { min: THREE.Vector3; max: THREE.Vector3; mid: THREE.Vector3 };
    const found: Array<{ mesh: THREE.Mesh; of: Uint16Array; list: Piece[] }> = [];
    const fabric: Piece[] = [];
    for (const mesh of g.meshes) {
      // into room space as plain floats: a packed mesh stores 16-bit grid positions with the grid as its node's scale
      const src = mesh.geometry.getAttribute('position'), flat = new Float32Array(src.count * 3);
      for (let i = 0; i < src.count; i++) { flat[i * 3] = src.getX(i); flat[i * 3 + 1] = src.getY(i); flat[i * 3 + 2] = src.getZ(i); }
      mesh.geometry.setAttribute('position', new THREE.BufferAttribute(flat, 3));
      mesh.geometry.applyMatrix4(mesh.matrixWorld);
      mesh.matrix.identity();
      mesh.updateMatrixWorld(true);
    }
    for (const mesh of g.meshes) {
      const geo = mesh.geometry, pos = geo.getAttribute('position');
      const { of, count } = pieces(geo);
      const list: Piece[] = Array.from({ length: count }, () => ({ min: new THREE.Vector3(Infinity, Infinity, Infinity), max: new THREE.Vector3(-Infinity, -Infinity, -Infinity), mid: new THREE.Vector3() }));
      const v = new THREE.Vector3();
      for (let i = 0; i < pos.count; i++) { v.fromBufferAttribute(pos, i); list[of[i]].min.min(v); list[of[i]].max.max(v); }
      for (const p of list) { p.mid.addVectors(p.min, p.max).multiplyScalar(0.5); if (p.max.y - p.min.y > 1) fabric.push(p); }
      found.push({ mesh, of, list });
    }
    if (fabric.length !== 2) return;   // not the expected two panels: leave them hanging
    fabric.sort((a, b) => a.mid.x - b.mid.x);
    const [L, R] = fabric, middle = (L.max.x + R.min.x) / 2;
    this.curtains.sides = [
      { pivot: L.min.x, scale: (middle + CURTAIN_OVERLAP - L.min.x) / (L.max.x - L.min.x), depth: L.mid.z },
      { pivot: R.max.x, scale: (R.max.x - (middle - CURTAIN_OVERLAP)) / (R.max.x - R.min.x), depth: R.mid.z },
    ];
    for (const { mesh, of, list } of found) {
      const pos = mesh.geometry.getAttribute('position') as THREE.BufferAttribute;
      const side = new Int8Array(pos.count), ring = new Float32Array(pos.count);
      for (let i = 0; i < pos.count; i++) {
        const p = list[of[i]];
        side[i] = p.mid.x < middle ? 0 : 1;
        ring[i] = fabric.includes(p) ? NaN : p.mid.x;   // a ring slides as a whole, by where its centre goes
      }
      pos.setUsage(THREE.DynamicDrawUsage);
      this.curtains.panels.push({ mesh, rest: Float32Array.from(pos.array as Float32Array), side, ring });
    }
    this.curtains.group = g;
  }

  private toggleCurtains() {
    const c = this.curtains;
    if (!c.panels.length) return;
    c.closed = !c.closed;
    this.toast(c.closed ? 'Curtains closed' : 'Curtains open', 1400);
    const g = c.group;
    if (g) {
      g.def = { ...g.def, label: c.closed ? 'Curtains · tap to open' : 'Curtains · tap to close' };
      if (this.hovered === g) this.ui.label.textContent = g.def.label;
    }
  }

  private animateCurtains(dt: number) {
    const c = this.curtains, target = c.closed ? 1 : 0;
    if (!c.panels.length || c.level === target) return;
    c.level = THREE.MathUtils.clamp(c.level + Math.sign(target - c.level) * Math.min(dt, 0.05) / CURTAIN_TIME, 0, 1);
    if (Math.abs(c.level - target) < 1e-3) c.level = target;
    const e = ease(c.level);
    for (const p of c.panels) {
      const pos = p.mesh.geometry.getAttribute('position') as THREE.BufferAttribute, a = pos.array as Float32Array;
      for (let i = 0; i < pos.count; i++) {
        const s = c.sides[p.side[i]], k = 1 + (s.scale - 1) * e, x = p.rest[i * 3], z = p.rest[i * 3 + 2], rc = p.ring[i];
        if (Number.isNaN(rc)) {
          a[i * 3] = s.pivot + (x - s.pivot) * k;
          a[i * 3 + 2] = s.depth + (z - s.depth) * (1 - 0.45 * e);   // spread pleats are shallower
        } else {
          a[i * 3] = x + (s.pivot + (rc - s.pivot) * k - rc);
        }
      }
      pos.needsUpdate = true;
      p.mesh.geometry.computeBoundingSphere();
      p.mesh.geometry.computeBoundingBox();
    }
    const g = c.group;
    if (g) { g.box.makeEmpty(); g.meshes.forEach((m) => g.box.expandByObject(m)); }
  }

  private placeDots() {
    const show = !this.focused && !this.tween;
    this.ui.dots.classList.toggle('hidden', !show);
    if (!show) return;
    const v = new THREE.Vector3();
    for (const g of this.groups.values()) {
      if (!g.dot) continue;
      g.box.getCenter(v);
      v.y = g.box.max.y + 0.06;
      v.project(this.camera);
      const behind = v.z > 1;
      g.dot.style.transform = `translate(${((v.x + 1) / 2) * innerWidth}px, ${((1 - v.y) / 2) * innerHeight}px)`;
      g.dot.style.visibility = behind ? 'hidden' : '';
    }
  }
}
